import useAnalytics from "hooks/analytics";
import React, { useState } from "react";
import {
    trackBookingFlowLabSelected,
    trackBookingFlowLocationPopupPopoverClicked,
    trackBookingFlowLocationPopupToggleClicked,
} from "utils/analytics/booking-flow-tracking";
import { ReactComponent as CheckIcon } from "../../assets/images/check-green.svg";
import { ReactComponent as ClockIcon } from "../../assets/images/timer.svg";
import {
    BookingFlowCardGrid,
    BookingFlowLayout,
    BookingFlowPageHeading,
    BookingFlowPrimaryButton,
    bookingFlowSelectableCardStyles,
    BookingFlowSelectableCard,
} from "../../components/booking-flow";
import useLanguage from "../../hooks/language";
import styles from "./lab-selection-v2.module.css";
import RamqPopover, { LabType } from "./ramqPopover";
import footnoteStyles from "../../components/booking-flow/booking-flow-primary-button.module.css";

export default function LabSelectionPageV2({
    handleLabSelectionSubmission,
    labType,
    setLabType,
}: {
    handleLabSelectionSubmission: (labType: LabType) => void;
    labType: LabType | undefined;
    setLabType: (arg: LabType) => void;
}): React.ReactElement {
    const { translations } = useLanguage();
    const t = translations.bookingFlow.labSelection;
    const tAvailability = translations.availabilityPublicPage;
    const [analytics] = useAnalytics();
    const [isRAMQPopoverOpen, setIsRAMQPopoverOpen] = useState<boolean>(false);

    const onPopoverCtaClick = () => {
        trackBookingFlowLocationPopupPopoverClicked(analytics);
    };
    const onPopoverToggle = () => {
        trackBookingFlowLocationPopupToggleClicked(analytics);
    };

    return (
        <BookingFlowLayout
            currentStep="lab"
            skipConsultationStep={labType === "private"}
        >
            <BookingFlowPageHeading
                title={t.title}
                subtitle={t.subtitle}
                responsive
                subtitleFullWidth
            />

            <BookingFlowCardGrid>
                <BookingFlowSelectableCard
                    checked={labType === "public"}
                    onSelect={() => setLabType("public")}
                    icon={<ClockIcon />}
                    eyebrow={t.publicEyebrow}
                    headline={t.publicHeadline}
                    priceChip={t.publicPriceChip}
                >
                    <p
                        className={bookingFlowSelectableCardStyles.bodyText}
                        dangerouslySetInnerHTML={{
                            __html: t.publicDescription,
                        }}
                    />
                </BookingFlowSelectableCard>

                <BookingFlowSelectableCard
                    checked={labType === "private"}
                    onSelect={() => setLabType("private")}
                    variant="dark"
                    eyebrow={t.privateEyebrow}
                    headline={
                        <>
                            {t.privateHeadline}
                            <sup
                                style={{
                                    fontSize: 12,
                                    top: -14,
                                    position: "relative",
                                }}
                            >
                                1
                            </sup>
                        </>
                    }
                    priceChip={t.privatePriceChip}
                >
                    <ul className={bookingFlowSelectableCardStyles.bulletList}>
                        {[t.privateBenefitOne, t.privateBenefitTwo].map(
                            (text) => (
                                <li
                                    key={text}
                                    className={
                                        bookingFlowSelectableCardStyles.bulletRow
                                    }
                                >
                                    <CheckIcon
                                        className={
                                            bookingFlowSelectableCardStyles.bulletIcon
                                        }
                                        aria-hidden
                                    />
                                    <span
                                        className={
                                            bookingFlowSelectableCardStyles.bodyTextDark
                                        }
                                    >
                                        {text}
                                    </span>
                                </li>
                            )
                        )}
                        <li
                            className={
                                bookingFlowSelectableCardStyles.bulletRow
                            }
                        >
                            <CheckIcon
                                className={
                                    bookingFlowSelectableCardStyles.bulletIcon
                                }
                                aria-hidden
                            />
                            <span
                                className={
                                    bookingFlowSelectableCardStyles.bodyTextDark
                                }
                                dangerouslySetInnerHTML={{
                                    __html: t.privateBenefitThree,
                                }}
                            />
                        </li>
                    </ul>
                </BookingFlowSelectableCard>
            </BookingFlowCardGrid>

            <p className={footnoteStyles.footnote}>{t.footnote}</p>

            {labType === "public" && (
                <div className={styles.ramqRow}>
                    <span className={styles.ramqLabel}>
                        {tAvailability.ramqValidLabel}
                    </span>
                    <RamqPopover
                        isRAMQPopoverOpen={isRAMQPopoverOpen}
                        setIsRAMQPopoverOpen={setIsRAMQPopoverOpen}
                        setLabType={setLabType}
                        onCtaClick={onPopoverCtaClick}
                        onToggle={onPopoverToggle}
                    />
                </div>
            )}

            <BookingFlowPrimaryButton
                disabled={!labType}
                onClick={() => {
                    if (!labType) {
                        return;
                    }
                    trackBookingFlowLabSelected(analytics, labType);
                    handleLabSelectionSubmission(labType);
                }}
            >
                {t.nextButton}
            </BookingFlowPrimaryButton>
        </BookingFlowLayout>
    );
}
